import { useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import ToolTrace from './ToolTrace';
import { isRedundantMarker, replaceMarkers, unwrapBracketedLinks } from './chatLinks';
import TimelineCard from './TimelineCard';
import {
  SentencingRecordsCard, SentencingSummaryCard, SentencingAuthorityCard,
} from './SentencingCards';
import WitnessGraphCard from './WitnessGraphCard';
import EntryRefinementCard from './EntryRefinementCard';
import BuildPlanCard from './BuildPlanCard';
import EventDraftCard from './EventDraftCard';
import MergeProposalCard from './MergeProposalCard';
import EntrySplitCard from './EntrySplitCard';
import FileDocumentsCard from './FileDocumentsCard';
import ExportPackageCard from './ExportPackageCard';
import { SavedWorkCard } from './SavedWork';
import { WitnessExportCard } from './WitnessExportMenu';
import SourcesPanel from './SourcesPanel';
import { AGENT_NAME } from '../brand';
import { useNotepad } from '../contexts/NotepadContext';
import NotebookDestinationSelect from './NotebookDestinationSelect';
import { useApp } from '../contexts/AppContext';

// Typed-renderer registry: dispatch on `tool_trace[*].tool` to a card
// that consumes the structured result envelope. The LLM's prose is the
// narrative caption; the card owns the structured artifact. Registry
// composes per-tool with zero cross-tool coupling — to add a card for
// e.g. find_contradictions, drop a new entry here.
const TOOL_CARDS = Object.freeze({
  build_timeline: TimelineCard,
  find_sentences: SentencingRecordsCard,
  query_sentencing_records: SentencingRecordsCard,   // answers saved before the finder (2026-10-10)
  // get_sentencing_facets is Arc's lookup of valid filter values, not an answer: each count
  // there ignores its own filter, so as a card it contradicted the answer (owner, 2026-10-06).
  summarize_sentencing_records: SentencingSummaryCard,
  search_sentencing_authority: SentencingAuthorityCard,
  show_witness_graph: WitnessGraphCard,
  propose_entry_refinement: EntryRefinementCard,
  propose_build_plan: BuildPlanCard,
  propose_events_from_sources: EventDraftCard,
  propose_entry_merges: MergeProposalCard,
  propose_entry_split: EntrySplitCard,
  correct_file_documents: FileDocumentsCard,
  prepare_export_package: ExportPackageCard,
  save_artifact: SavedWorkCard,
  update_artifact: SavedWorkCard,
  export_witness_list: WitnessExportCard,
});
// Tools where a later call in the same message SUPERSEDES earlier ones
// (verification-retry loops re-call with fixed quotes; each attempt used to
// render its own diff card). Distinct-call tools (two sentencing queries)
// keep one card per call.
const TOOL_CARDS_LAST_WINS = Object.freeze(new Set(['propose_entry_refinement', 'propose_build_plan', 'propose_events_from_sources', 'propose_entry_merges', 'propose_entry_split']));
import {
  buildEvidenceUrl,
  buildEvidenceLabel,
  timestampToSeconds,
  isLinkable,
} from '../domain/sourceRef';

const FILE_EXT = '(?:pdf|doc|docx|txt|rtf|csv|xlsx|xls|mp3|mp4|mov|wav|m4a|ogg|webm|avi|mkv|png|jpg|jpeg|tiff|bmp|eml|msg|zip)';

/** Resolve a filename to a document object (case-insensitive, suffix/stem/contains match). */
function resolveDoc(name, filenameMap) {
  if (!name || !filenameMap) return null;
  const lower = name.trim().toLowerCase();

  // 1. Exact match (case-insensitive)
  for (const [k, v] of Object.entries(filenameMap)) {
    if (k.toLowerCase() === lower) return v;
  }

  // 2. Stem match (cited name without extension matches a key's stem)
  const citedStem = lower.replace(/\.[^.]+$/, '');
  for (const [k, v] of Object.entries(filenameMap)) {
    const kStem = k.replace(/\.[^.]+$/, '').toLowerCase();
    if (kStem === lower || kStem === citedStem) return v;
  }

  // 3. Suffix match — cited name is the tail of a longer stored filename
  //    (handles path prefixes the agent may drop, e.g. "report.pdf" matches "uploads/report.pdf")
  for (const [k, v] of Object.entries(filenameMap)) {
    const kLower = k.toLowerCase();
    if (kLower.endsWith('/' + lower) || kLower.endsWith('\\' + lower)) return v;
  }

  // 4. Normalize whitespace/special chars — collapse spaces, strip parens suffixes
  //    e.g. "Report  (1).pdf" → "report 1.pdf"
  const norm = s => s.replace(/[()]/g, '').replace(/\s+/g, ' ').trim();
  const citedNorm = norm(lower);
  for (const [k, v] of Object.entries(filenameMap)) {
    if (norm(k.toLowerCase()) === citedNorm) return v;
  }

  return null;
}

/**
 * Render a SourceRef-shaped citation as a markdown link.
 * Returns null if the ref is unlinkable (no document_id).
 */
function refToMarkdownLink(ref, pathname) {
  if (!isLinkable(ref)) return null;
  const href = buildEvidenceUrl(ref, pathname);
  const label = buildEvidenceLabel(ref);
  // ⚠ marks citations the backend couldn't verify against indexed chunks
  // (typically read_doc / list-only sources). Still clickable so users
  // can open the doc, just with a "trust but verify" affordance.
  const glyph = ref.verified === false ? '⚠' : '📄';
  return href ? `[${glyph} ${label}](${href})` : null;
}

/**
 * Sideband path: model emitted [N] markers + a structured citations
 * block. Each `cite` is already SourceRef-shaped (built by the
 * backend verifier) — we just replace [N] markers with markdown
 * links using the shared lib.
 *
 * Returns null when no valid sideband refs are available, so the
 * caller falls back to the regex path.
 */
function preprocessSidebandCitations(text, citations, pathname) {
  if (!text || !citations || citations.length === 0) return null;

  // Include unverified refs in the map: stripping them leaves orphan [N]
  // markers in the body, which the user reads as broken footnotes. We
  // surface the unverified state via the link glyph instead (see
  // refToMarkdownLink — verified=true uses 📄, false uses ⚠).
  const refMap = {};
  for (const cite of citations) {
    if (cite.ref != null && isLinkable(cite)) {
      refMap[cite.ref] = cite;
    }
  }
  if (Object.keys(refMap).length === 0) return null;

  // [N], [N, M, ...] and [N-M] (range syntax slips in despite prompt guidance against it;
  // it is expanded client-side rather than failing closed on the user's screen).
  const seenHrefs = new Set();
  const lowerText = text.toLowerCase();
  return replaceMarkers(text, (nums) => {
    const refs = nums.map(n => refMap[n]).filter(Boolean);
    const links = refs
      // a marker that only repeats "this document" for a document the answer names is dropped:
      // the name itself becomes the link (linkifyProseMentions)
      .filter(ref => !isRedundantMarker(ref, lowerText))
      .map(ref => ({ ref, link: refToMarkdownLink(ref, pathname) }))
      .filter(x => x.link)
      .filter(({ link }) => {
        const hrefMatch = link.match(/\(([^)]+)\)/);
        const href = hrefMatch?.[1];
        if (!href || seenHrefs.has(href)) return false;
        seenHrefs.add(href);
        return true;
      })
      .map(x => x.link);
    // no resolvable refs in the group → the literal [N] stays in the text
    return { links, known: refs.length > 0 };
  });
}

/**
 * Regex path: model wrote `[file.pdf, p.7]` directly in prose. For
 * each match, look up a SourceRef-shaped citation from msg.citations
 * (preferring same filename + same page) so we get a snippet to
 * highlight. If no SourceRef matches, fall back to the filenameMap
 * for at least the document_id.
 *
 * Both inputs are optional; if both are missing we return the text
 * unchanged.
 */
function preprocessCitations(text, filenameMap, citations, pathname) {
  if (!text) return '';
  const hasMap = filenameMap && Object.keys(filenameMap).length > 0;
  const hasCites = citations && citations.length > 0;
  if (!hasMap && !hasCites) return text;

  const citationRe = new RegExp(
    `\\[([^\\[\\],]+\\.${FILE_EXT})(?:,?\\s*(?:pp?\\.?\\s*(\\d+)(?:\\s*[-,]\\s*(?:pp?\\.?\\s*)?\\d+)*|(\\d{1,2}:\\d{2}(?::\\d{2})?)))?\\]`,
    'gi'
  );

  return text.replace(citationRe, (match, filename, page, timestamp) => {
    const cleanFn = filename.trim();
    const pageNum = page ? Math.max(1, parseInt(page, 10)) : null;
    const tsSecs = timestamp ? timestampToSeconds(timestamp) : null;

    // Find best SourceRef from msg.citations: same filename, prefer
    // same page. Carries the snippet through to the URL.
    let ref = null;
    if (hasCites) {
      const normFn = cleanFn.toLowerCase();
      const sameFile = citations.filter(c => (c.filename || '').toLowerCase() === normFn);
      if (pageNum != null) {
        ref = sameFile.find(c => c.page === pageNum) || null;
      }
      if (!ref) ref = sameFile[0] || null;
    }

    // Fallback: filenameMap gives us the document_id when no SourceRef
    // is available. We construct a minimal SourceRef so the URL builder
    // still works (empty snippet, no highlight).
    if (!ref && hasMap) {
      const doc = resolveDoc(cleanFn, filenameMap);
      if (doc) {
        ref = {
          document_id: doc.id,
          filename: cleanFn,
          page: pageNum,
          timestamp: tsSecs,
          snippet: '',
          kind: 'none',
          verified: false,
        };
      }
    }

    if (!ref) return match; // leave unresolved citations as-is

    // The regex match may carry a page/timestamp the SourceRef doesn't.
    // Synthesize a final ref that includes what the prose says, while
    // letting the SourceRef's snippet/document_id win.
    const finalRef = {
      ...ref,
      page: pageNum ?? ref.page,
      timestamp: tsSecs ?? ref.timestamp,
    };
    const link = refToMarkdownLink(finalRef, pathname);
    return link || match;
  });
}

/**
 * Prose-mention path: the model referenced a case document by name in
 * plain prose — parenthesized or bare, usually without the extension —
 * rather than the bracketed [filename.ext, p.N] citation form. Match
 * known filenames/stems from the case's filenameMap directly in the
 * text and linkify every mention.
 *
 * Guards against false positives: candidates must be ≥ MIN_STEM chars,
 * matching is longest-first (so "GEO Fence Part Two" wins over "GEO
 * Fence"), and segments that are already markdown links from the
 * earlier passes are left untouched.
 */
const MIN_STEM = 8;

function linkifyProseMentions(text, filenameMap, citations, pathname) {
  if (!text || !filenameMap) return text;
  const candidates = Object.keys(filenameMap)
    .filter(k => k.trim().length >= MIN_STEM)
    .sort((a, b) => b.length - a.length);
  if (!candidates.length) return text;

  const escaped = candidates.map(c => c.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  const mentionRe = new RegExp(`(?:${escaped.join('|')})`, 'gi');

  // Split on full markdown-link syntax; captured link tokens land at odd
  // indices and pass through untouched.
  const parts = text.split(/(\[[^\]]*\]\([^)]*\))/g);
  for (let i = 0; i < parts.length; i += 2) {
    parts[i] = parts[i].replace(mentionRe, (m) => {
      const doc = resolveDoc(m, filenameMap);
      if (!doc) return m;
      // Prefer a SourceRef for this doc so the link carries a snippet
      // (anchors the viewer highlight); fall back to a minimal ref.
      const byDoc = (citations || []).find(c => c.document_id === doc.id);
      const ref = byDoc || {
        document_id: doc.id,
        filename: doc.filename || m,
        page: null,
        snippet: '',
        kind: 'none',
        verified: false,
      };
      return refToMarkdownLink(ref, pathname) || m;
    });
  }
  return parts.join('');
}

function ConfidenceBadge({ confidence }) {
  if (confidence == null) return null;
  const pct = Math.round(confidence * 100);
  let cls, icon;
  if (pct >= 80) { cls = 'confidence-high'; icon = '🟢'; }
  else if (pct >= 50) { cls = 'confidence-medium'; icon = '🟡'; }
  else { cls = 'confidence-low'; icon = '🔴'; }

  return (
    <span className={`confidence-badge ${cls}`} title={`Confidence: ${pct}%`}>
      {icon} {pct}%
    </span>
  );
}

function CostMeta({ msg, marginMultiplier = 1 }) {
  if (!msg.cost_usd && !msg.tokens_in) return null;
  const cost = msg.cost_usd ? (msg.cost_usd * marginMultiplier).toFixed(4) : null;
  // Cost and time are honest, user-actionable numbers; raw token counts are
  // engine telemetry — keep them as a hover detail, not headline copy.
  const tokenTip = [
    msg.tokens_in != null && `${msg.tokens_in.toLocaleString()} tokens read`,
    msg.tokens_out != null && `${msg.tokens_out.toLocaleString()} tokens written`,
  ].filter(Boolean).join(' · ');
  return (
    <div className="chat-cost-meta" title={tokenTip || undefined}>
      {cost && <span>${cost}</span>}
      {msg.duration_ms != null && <span>{(msg.duration_ms / 1000).toFixed(1)}s</span>}
    </div>
  );
}

export default function ChatMessage({ msg, filenameMap, marginMultiplier, token, isFollowUp = false, messageIndex = 0, citations, hideToolTrace = false }) {
  const isUser = msg.role === 'user';
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const notepad = useNotepad();
  const { chatConversationId } = useApp();
  const [savedToNotepad, setSavedToNotepad] = useState(false);
  const [saveError, setSaveError] = useState(false);

  const saveToNotepad = async () => {
    if (!notepad) return;
    const allCites = citations || msg.citations;
    // captureClip resolves to the created clip, or null on failure (errors are
    // swallowed into notepad.error). Only confirm "✓ Saved" when a clip really
    // came back, so a failed save can't masquerade as success.
    const clip = await notepad.captureClip({
      kind: 'chat',
      quote: msg.content || '',
      citations: allCites && allCites.length ? allCites : undefined,
      conversation_id: chatConversationId || undefined,
      message_id: msg.id != null ? String(msg.id) : undefined,
    });
    if (clip) {
      setSavedToNotepad(true);
      setTimeout(() => setSavedToNotepad(false), 2000);
    } else {
      setSaveError(true);
      setTimeout(() => setSaveError(false), 3000);
    }
  };

  // Preprocess citations: try sideband first (document_id-based), fall back to regex.
  // `token` is accepted for backwards compat but no longer threaded through —
  // links now point at the SPA viewer, which uses the existing Firebase session.
  const processed = useMemo(
    () => {
      const content = msg.content || '';
      const allCites = citations || msg.citations;
      const sidebandResult = preprocessSidebandCitations(content, allCites, pathname);
      const base = sidebandResult ?? preprocessCitations(content, filenameMap, allCites, pathname);
      // Final pass: bare/parenthesized document names in prose become
      // links too (models cite naturally despite the bracket contract;
      // partial-turn bubbles have no citations array at all).
      // …and a file name the model put in square brackets keeps only the link.
      return unwrapBracketedLinks(linkifyProseMentions(base, filenameMap, allCites, pathname));
    },
    [msg.content, msg.citations, citations, filenameMap, pathname]
  );

  return (
    <div
      className={`chat-message ${isUser ? 'chat-message-user' : 'chat-message-assistant'}`}
      // Lets SelectionClipper turn a selection inside an Arc answer into a
      // linkable `chat` clip (instead of an anchorless note).
      {...(!isUser && chatConversationId
        ? {
            'data-np-chat-conversation': chatConversationId,
            ...(msg.id != null ? { 'data-np-chat-message': String(msg.id) } : {}),
          }
        : {})}
    >
      <div className="chat-message-header">
        <span className="chat-role">{isUser ? 'You' : AGENT_NAME}</span>
        {isUser && isFollowUp && <span className="chat-followup-badge">↩ Follow-up</span>}
        {!isUser && <ConfidenceBadge confidence={msg.confidence} />}
      </div>

      <div className="chat-message-body">
        {isUser ? (
          <p>{msg.content}</p>
        ) : (
          <ReactMarkdown
            components={{
              // In-app links (same-origin paths from buildEvidenceUrl, or
              // legacy HashRouter hashes) navigate via React Router so the
              // viewer modal pops over the chat without a full page reload.
              // A full reload re-mounts AuthProvider, which has to `await
              // fetchHealth()` before the token is available — viewer fetches
              // fire during that window and 401 ("Unauthorized"). External
              // links keep new-tab + noopener.
              a: ({ href, children, ...props }) => {
                const isInApp = typeof href === 'string' && (
                  href.startsWith('/') || href.startsWith('?') || href.startsWith('#')
                );
                if (isInApp) {
                  return (
                    <a
                      href={href}
                      className="citation-link"
                      onClick={(e) => {
                        // Let modifier-clicks (cmd/ctrl/shift) and middle-click
                        // keep their default new-tab/window behavior so power
                        // users can still split-screen citations.
                        if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
                        e.preventDefault();
                        navigate(href);
                      }}
                      {...props}
                    >
                      {children}
                    </a>
                  );
                }
                return (
                  <a href={href} target="_blank" rel="noopener noreferrer" className="citation-link" {...props}>
                    {children}
                  </a>
                );
              },
            }}
          >
            {processed}
          </ReactMarkdown>
        )}
      </div>

      {!isUser && (citations || msg.citations) && (
        <SourcesPanel
          citations={citations || msg.citations}
          pathname={pathname}
        />
      )}

      {!isUser && !hideToolTrace && msg.tool_trace?.length > 0 && (
        <>
          {msg.tool_trace.map((call, i) => {
            // Live SSE shape uses `tool_name`; persisted shape uses
            // `tool` (see agent_session.persist_assistant_response).
            const name = call.tool_name || call.tool;
            const Card = TOOL_CARDS[name];
            if (!Card || !call.result) return null;
            if (TOOL_CARDS_LAST_WINS.has(name)) {
              for (let j = i + 1; j < msg.tool_trace.length; j += 1) {
                const later = msg.tool_trace[j];
                if ((later.tool_name || later.tool) === name && later.result) return null;
              }
            }
            return <Card key={`card-${i}`} result={call.result} />;
          })}
          <ToolTrace trace={msg.tool_trace} />
        </>
      )}

      {!isUser && (
        <div className="chat-message-footer">
          {/* Clips are case-scoped — outside a case (help drawer, sentencing
              workspace) captureClip would silently no-op, so hide the button. */}
          {notepad?.caseId && msg.content && (
            <button
              className={`chat-save-clip${saveError ? ' chat-save-clip-error' : ''}`}
              title="Save this answer to your notepad"
              onClick={saveToNotepad}
              disabled={savedToNotepad}
            >
              {saveError ? '⚠ Save failed — retry' : (savedToNotepad ? '✓ Saved' : '📌 Save to notepad')}
            </button>
          )}
          {notepad && msg.content && <NotebookDestinationSelect />}
          <CostMeta msg={msg} marginMultiplier={marginMultiplier} />
        </div>
      )}
    </div>
  );
}
